import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getUserById, updateUserDetails } from "../../services/userService";
import { getUserOrders } from "../../services/orderService";
import { getAllUsers } from "../../services/adminRechargeService";
import { toast } from "react-hot-toast";
import {
  ArrowLeft,
  User,
  Mail,
  Phone,
  MapPin,
  Activity,
  Heart,
  TrendingUp,
  Shield,
  Clock,
  Package,
  BarChart3,
  Droplets,
  Flame,
  Target,
  Calendar,
  CreditCard,
  ShoppingBag,
  Award,
  Cake,
  Users,
  Edit2,
  Save,
  X,
} from "lucide-react";

export default function AdminUserDetails() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [orderFilter, setOrderFilter] = useState("All");

  const [referrals, setReferrals] = useState([]);
  const [referralsLoading, setReferralsLoading] = useState(false);

  const [sponsoredUsers, setSponsoredUsers] = useState([]);
  const [sponsoredLoading, setSponsoredLoading] = useState(false);

  // Monthly Report
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [monthlyReportData, setMonthlyReportData] = useState(null);
  const [monthlyReportLoading, setMonthlyReportLoading] = useState(false);

  // Inventory Edit State
  const [isEditingInventory, setIsEditingInventory] = useState(false);
  const [inventoryForm, setInventoryForm] = useState({
    remainingShakes: 0,
    complementaryShakeQuantity: 0,
    complementaryValidityDate: "",
    shakePackage: "",
    hasPurchasedPackage: false
  });
  const [isSavingInventory, setIsSavingInventory] = useState(false);

  useEffect(() => {
    fetchUserDetails();
    fetchUserOrders();
  }, [userId]);

  useEffect(() => {
    if (activeTab === "referrals" && user && referrals.length === 0) {
      fetchReferrals();
    }
    if (activeTab === "sponsored" && user && sponsoredUsers.length === 0) {
      fetchSponsored();
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (activeTab === "monthly" && user) {
      fetchMonthlyReport();
    }
  }, [activeTab, reportMonth, user]);

  const fetchReferrals = async () => {
    if (!user) return;
    try {
      setReferralsLoading(true);

      const allUsers = await getAllUsers();

      const uid = user.id?.toLowerCase() || "";
      const mobile = user.personalInformation?.mobileNumber || user.personalDetails?.mobile || "";
      const fName = (user.personalInformation?.fullName || user.personalDetails?.name || "").toLowerCase();

      const uniqueReferrals = allUsers.filter(u => {
        // Skip self
        if (u.id === user.id) return false;

        const refBy = (u.accountSecurity?.referredBy || u.referredBy || "").toLowerCase();

        if (!refBy) return false;

        // Match against ID, Mobile, or Name (case-insensitive)
        return refBy === uid || (mobile && refBy === mobile) || (fName && refBy === fName);
      });

      // For each referred user, calculate their top-up stats
      const referralsWithStats = await Promise.all(uniqueReferrals.map(async (refUser) => {
        try {
          const refOrders = await getUserOrders(refUser.id);

          const topUpGuestQty = refOrders
            .filter(o => o.transaction?.type?.toLowerCase() === "topup guest shake")
            .reduce((sum, o) => sum + (o.summary?.totalShakeQuantity || 0), 0);

          const topUpAssociateQty = refOrders
            .filter(o => o.transaction?.type?.toLowerCase() === "topup associate shake")
            .reduce((sum, o) => sum + (o.summary?.totalShakeQuantity || 0), 0);

          return {
            ...refUser,
            stats: {
              topUpGuestQty,
              topUpAssociateQty,
              totalOrders: refOrders.length
            }
          };
        } catch (e) {
          console.error(`Error fetching orders for referred user ${refUser.id}:`, e);
          return { ...refUser, stats: { topUpGuestQty: 0, topUpAssociateQty: 0, totalOrders: 0 } };
        }
      }));

      setReferrals(referralsWithStats);
    } catch (error) {
      console.error("Error fetching referrals:", error);
      toast.error("Failed to fetch referrals");
    } finally {
      setReferralsLoading(false);
    }
  };

  const fetchSponsored = async () => {
    if (!user) return;
    try {
      setSponsoredLoading(true);

      const allUsers = await getAllUsers();

      const uid = user.id?.toLowerCase() || "";
      const mobile = user.personalInformation?.mobileNumber || user.personalDetails?.mobile || "";
      const fName = (user.personalInformation?.fullName || user.personalDetails?.name || "").toLowerCase();

      const uniqueSponsored = allUsers.filter(u => {
        // Skip self
        if (u.id === user.id) return false;

        const sponBy = (u.accountSecurity?.sponsoredBy || u.sponsoredBy || u.herbalifeDetails?.sponsorName || "").toLowerCase();

        if (!sponBy) return false;

        // Match against ID, Mobile, or Name (case-insensitive)
        return sponBy === uid || (mobile && sponBy === mobile) || (fName && sponBy === fName);
      });

      // For each sponsored user, calculate their top-up stats
      const sponsoredWithStats = await Promise.all(uniqueSponsored.map(async (sponUser) => {
        try {
          const sponOrders = await getUserOrders(sponUser.id);

          const topUpGuestQty = sponOrders
            .filter(o => o.transaction?.type?.toLowerCase() === "topup guest shake")
            .reduce((sum, o) => sum + (o.summary?.totalShakeQuantity || 0), 0);

          const topUpAssociateQty = sponOrders
            .filter(o => o.transaction?.type?.toLowerCase() === "topup associate shake")
            .reduce((sum, o) => sum + (o.summary?.totalShakeQuantity || 0), 0);

          return {
            ...sponUser,
            stats: {
              topUpGuestQty,
              topUpAssociateQty,
              totalOrders: sponOrders.length
            }
          };
        } catch (e) {
          console.error(`Error fetching orders for sponsored user ${sponUser.id}:`, e);
          return { ...sponUser, stats: { topUpGuestQty: 0, topUpAssociateQty: 0, totalOrders: 0 } };
        }
      }));

      setSponsoredUsers(sponsoredWithStats);
    } catch (error) {
      console.error("Error fetching sponsored users:", error);
      toast.error("Failed to fetch sponsored users");
    } finally {
      setSponsoredLoading(false);
    }
  };

  const fetchMonthlyReport = async () => {
    if (!user) return;
    try {
      setMonthlyReportLoading(true);

      const allUsers = await getAllUsers();
      const uid = user.id?.toLowerCase() || "";
      const mobile = user.personalInformation?.mobileNumber || user.personalDetails?.mobile || "";
      const fName = (user.personalInformation?.fullName || user.personalDetails?.name || "").toLowerCase();

      const uniqueReferrals = allUsers.filter(u => {
        if (u.id === user.id) return false;
        const refBy = (u.accountSecurity?.referredBy || u.referredBy || "").toLowerCase();
        if (!refBy) return false;
        return refBy === uid || (mobile && refBy === mobile) || (fName && refBy === fName);
      });

      const [y, m] = reportMonth.split('-');
      const reportYear = parseInt(y, 10);
      const reportM = parseInt(m, 10) - 1;

      // Personal Consumption
      const ownOrders = await getUserOrders(user.id);
      const ownGuest = ownOrders
        .filter(o => {
          const d = new Date(o.timestamp || o.createdAt);
          return o.transaction?.type === "Guest Shake" && d.getMonth() === reportM && d.getFullYear() === reportYear;
        })
        .reduce((sum, o) => sum + getOrderQuantitySum(o), 0);

      const ownAssociate = ownOrders
        .filter(o => {
          const d = new Date(o.timestamp || o.createdAt);
          return o.transaction?.type === "Associate Shake" && d.getMonth() === reportM && d.getFullYear() === reportYear;
        })
        .reduce((sum, o) => sum + getOrderQuantitySum(o), 0);

      // Referrals Consumption
      const refData = await Promise.all(uniqueReferrals.map(async (ru) => {
        try {
          const ruOrders = await getUserOrders(ru.id);
          const ruGuest = ruOrders
            .filter(o => {
              const d = new Date(o.timestamp || o.createdAt);
              return o.transaction?.type === "Guest Shake" && d.getMonth() === reportM && d.getFullYear() === reportYear;
            })
            .reduce((sum, o) => sum + getOrderQuantitySum(o), 0);

          const ruAssociate = ruOrders
            .filter(o => {
              const d = new Date(o.timestamp || o.createdAt);
              return o.transaction?.type === "Associate Shake" && d.getMonth() === reportM && d.getFullYear() === reportYear;
            })
            .reduce((sum, o) => sum + getOrderQuantitySum(o), 0);

          return {
            id: ru.id,
            name: ru.personalInformation?.fullName || ru.personalDetails?.name || "Unknown",
            mobile: ru.personalInformation?.mobileNumber || ru.personalDetails?.mobile || "N/A",
            guestCount: ruGuest,
            associateCount: ruAssociate
          };
        } catch (e) {
          console.error("Error for referral", ru.id, e);
          return { id: ru.id, name: ru.personalInformation?.fullName || "Unknown", mobile: "N/A", guestCount: 0, associateCount: 0 };
        }
      }));

      // Keep only those who had some consumption
      const activeReferrals = refData.filter(r => r.guestCount > 0 || r.associateCount > 0);

      const refGuestTotal = activeReferrals.reduce((sum, r) => sum + r.guestCount, 0);
      const refAssociateTotal = activeReferrals.reduce((sum, r) => sum + r.associateCount, 0);

      setMonthlyReportData({
        own: { guest: ownGuest, associate: ownAssociate },
        referrals: activeReferrals,
        refTotals: { guest: refGuestTotal, associate: refAssociateTotal }
      });

    } catch (e) {
      console.error(e);
      toast.error("Failed to load monthly report");
    } finally {
      setMonthlyReportLoading(false);
    }
  };

  const fetchUserDetails = async () => {
    try {
      setLoading(true);
      const userData = await getUserById(userId);
      setUser(userData);
    } catch (error) {
      toast.error("Failed to fetch user details");
      console.error("Error fetching user details:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditInventory = () => {
    const inv = user.inventory || {};
    setInventoryForm({
      remainingShakes: inv.remainingShakes ?? user.shakeQuantity ?? 0,
      complementaryShakeQuantity: inv.complementaryShakeQuantity || 0,
      complementaryValidityDate: inv.complementaryValidityDate || "",
      shakePackage: inv.shakePackage || "",
      hasPurchasedPackage: inv.hasPurchasedPackage || false
    });
    setIsEditingInventory(true);
  };

  const handleSaveInventory = async () => {
    try {
      setIsSavingInventory(true);
      const updatedInventory = {
        ...(user.inventory || {}),
        remainingShakes: Number(inventoryForm.remainingShakes),
        complementaryShakeQuantity: Number(inventoryForm.complementaryShakeQuantity),
        complementaryValidityDate: inventoryForm.complementaryValidityDate,
        shakePackage: inventoryForm.shakePackage,
        hasPurchasedPackage: inventoryForm.hasPurchasedPackage
      };

      await updateUserDetails(user.id, { inventory: updatedInventory });

      // Update local state
      setUser(prev => ({
        ...prev,
        inventory: updatedInventory
      }));

      setIsEditingInventory(false);
      toast.success("Inventory updated successfully");
    } catch (error) {
      console.error("Error updating inventory:", error);
      toast.error("Failed to update inventory");
    } finally {
      setIsSavingInventory(false);
    }
  };

  const fetchUserOrders = async () => {
    try {
      setOrdersLoading(true);
      const userOrders = await getUserOrders(userId);
      setOrders(userOrders);
    } catch (error) {
      console.error("Error fetching user orders:", error);
      toast.error("Failed to fetch user orders");
    } finally {
      setOrdersLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600 font-medium">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 font-medium">User not found</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const personalInfo = user.personalInformation || user.personalDetails || {};
  const bodyMeasurements = user.bodyMeasurements || user.dailyRecords || {};
  const herbalifeDetails = user.herbalifeDetails || {};
  const inventory = user.inventory || {};
  const currentHealthMetrics = user.currentHealthMetrics || {};
  const familyDetails = user.familyDetails || {};

  // -- Derived Statistics from Orders --
  const getOrderQuantitySum = (o) => {
    if (o.summary?.totalShakeQuantity) return o.summary.totalShakeQuantity;
    if (o.items && o.items.length > 0) {
      return o.items.reduce((s, i) => s + (i.quantity || 1), 0);
    }
    return 1;
  };

  const totalComplementaryQty = orders
    .filter(o => o.transaction?.type === "Trial")
    .reduce((sum, o) => sum + getOrderQuantitySum(o), 0);

  const totalTopupQty = orders
    .filter(o => o.transaction?.type === "TopUp Guest Shake" || o.transaction?.type === "TopUp Associate Shake")
    .reduce((sum, o) => sum + getOrderQuantitySum(o), 0);

  const totalGuestShakeQty = orders
    .filter(o => o.transaction?.type === "Guest Shake")
    .reduce((sum, o) => sum + getOrderQuantitySum(o), 0);

  const totalAssociateShakeQty = orders
    .filter(o => o.transaction?.type === "Associate Shake")
    .reduce((sum, o) => sum + getOrderQuantitySum(o), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-8">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(-1)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {personalInfo.fullName || personalInfo.name || "User Details"}
                </h1>
                <p className="text-sm text-gray-500 mt-1">
                  {personalInfo.mobileNumber || personalInfo.mobile || "No mobile"}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                {user.metadata?.isVerified || user.isVerified ? "Verified" : "Unverified"}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8 overflow-x-auto">
            {["overview", "health", "orders", "herbalife", "referrals", "sponsored", "monthly"].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-2 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${activeTab === tab
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        {activeTab === "overview" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Personal Information */}
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <User className="w-5 h-5 text-blue-600" />
                  Personal Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem icon={User} label="Full Name" value={personalInfo.fullName || personalInfo.name} />
                  <InfoItem icon={Phone} label="Mobile" value={personalInfo.mobileNumber || personalInfo.mobile} />
                  <InfoItem icon={Phone} label="WhatsApp" value={personalInfo.whatsappNumber} />
                  <InfoItem icon={Mail} label="Email" value={personalInfo.email} />
                  <InfoItem icon={MapPin} label="Address" value={personalInfo.address} className="md:col-span-2" />
                </div>
              </div>

              {/* Account Security */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="w-5 h-5 text-purple-600" />
                  Account Details
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem icon={Shield} label="Role" value={user.accountSecurity?.accountRole || user.role} />
                  <InfoItem icon={Target} label="Purpose" value={user.accountSecurity?.detailedPurpose || user.purpose} />
                  <InfoItem icon={User} label="Referred By" value={user.accountSecurity?.referredBy || user.referredBy} />
                  <InfoItem icon={User} label="Sponsored By" value={user.accountSecurity?.sponsoredBy || user.sponsoredBy || user.herbalifeDetails?.sponsorName} />
                  <InfoItem icon={Calendar} label="Joined" value={formatDate(user.metadata?.createdAt || user.createdAt)} />
                </div>
              </div>

              {/* Family & Important Dates */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Users className="w-5 h-5 text-pink-600" />
                  Family & Important Dates
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InfoItem icon={Cake} label="Birthday" value={familyDetails.birthday || "N/A"} />
                  <InfoItem icon={Heart} label="Marital Status" value={familyDetails.maritalStatus ? familyDetails.maritalStatus.charAt(0).toUpperCase() + familyDetails.maritalStatus.slice(1) : "N/A"} />
                  {familyDetails.maritalStatus === "married" && (
                    <>
                      <InfoItem icon={User} label="Spouse Name" value={familyDetails.spouseName} />
                      <InfoItem icon={Calendar} label="Marriage Date" value={familyDetails.marriageDate || "N/A"} />
                    </>
                  )}
                </div>

                {/* Children */}
                {familyDetails.children && familyDetails.children.length > 0 && (
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-500" />
                      Children ({familyDetails.children.length})
                    </h3>
                    <div className="space-y-2">
                      {familyDetails.children.map((child, index) => (
                        <div key={index} className="flex items-center justify-between p-3 bg-purple-50 rounded-xl">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                              <User className="w-4 h-4 text-purple-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{child.name || "N/A"}</p>
                              <p className="text-xs text-gray-500 capitalize">{child.gender || "N/A"}</p>
                            </div>
                          </div>
                          {child.birthday && (
                            <div className="flex items-center gap-1 text-xs text-purple-600">
                              <Cake className="w-3 h-3" />
                              {child.birthday}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="space-y-6">
              {/* Inventory Card */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl shadow-lg p-6 text-white relative">
                {!isEditingInventory ? (
                  <>
                    <button
                      onClick={handleEditInventory}
                      className="absolute top-4 right-4 p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-4 h-4 text-white" />
                    </button>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Shake Inventory</h3>
                      {/* <Package className="w-6 h-6 opacity-80" /> */}
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-blue-100 text-sm mb-1">Available Shakes</p>
                        <div className="text-3xl font-bold">
                          {inventory.remainingShakes ?? user.shakeQuantity ?? 0}
                        </div>
                      </div>
                      <div>
                        <p className="text-blue-100 text-sm mb-1">Package</p>
                        <div className="text-xl font-semibold mt-1 flex items-center gap-2">
                          {inventory.shakePackage || "None"}
                          {inventory.hasPurchasedPackage && (
                            <span className="text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded uppercase tracking-wide">Paid</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t border-white/20 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-blue-100 text-xs mb-1">Trial Shakes</p>
                        <p className="font-semibold">{inventory.complementaryShakeQuantity || 0}</p>
                      </div>
                      <div>
                        <p className="text-blue-100 text-xs mb-1">Valid Until</p>
                        <p className="font-semibold text-sm">{inventory.complementaryValidityDate ? formatDate(inventory.complementaryValidityDate) : "N/A"}</p>
                      </div>
                    </div>

                    <p className="text-blue-100 text-xs mt-4">
                      Last top-up: {formatDate(inventory.lastTopUpDate)}
                    </p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">Edit Inventory</h3>
                      <button
                        onClick={() => setIsEditingInventory(false)}
                        className="p-1 bg-white/20 hover:bg-white/30 rounded"
                      >
                        <X className="w-4 h-4 text-white" />
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-blue-100 mb-1">Available Shakes</label>
                        <input
                          type="number"
                          value={inventoryForm.remainingShakes}
                          onChange={(e) => setInventoryForm({ ...inventoryForm, remainingShakes: e.target.value })}
                          className="w-full px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-blue-100 mb-1">Shake Package</label>
                        <select
                          value={inventoryForm.shakePackage}
                          onChange={(e) => setInventoryForm({ ...inventoryForm, shakePackage: e.target.value })}
                          className="w-full px-3 py-1.5 bg-blue-600 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white [&>option]:bg-white [&>option]:text-gray-900"
                        >
                          <option value="">None</option>
                          <option value="Starter">Starter (1 Day)</option>
                          <option value="Growth">Growth (5 Days)</option>
                          <option value="Advanced">Advanced (15 Days)</option>
                          <option value="Ultimate">Ultimate (25 Days)</option>
                        </select>
                      </div>

                      <div className="flex items-center gap-2 mt-2">
                        <input
                          type="checkbox"
                          checked={inventoryForm.hasPurchasedPackage}
                          onChange={(e) => setInventoryForm({ ...inventoryForm, hasPurchasedPackage: e.target.checked })}
                          className="w-4 h-4 rounded border-white/20 bg-white/10 text-blue-600 focus:ring-blue-500"
                        />
                        <label className="text-xs text-blue-100">Paid Package Subscriber</label>
                      </div>

                      <div>
                        <label className="block text-xs text-blue-100 mb-1">Trial Shakes</label>
                        <input
                          type="number"
                          value={inventoryForm.complementaryShakeQuantity}
                          onChange={(e) => setInventoryForm({ ...inventoryForm, complementaryShakeQuantity: e.target.value })}
                          className="w-full px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white"
                        />
                      </div>

                      <div>
                        <label className="block text-xs text-blue-100 mb-1">Trial Valid Until</label>
                        <input
                          type="date"
                          value={inventoryForm.complementaryValidityDate}
                          onChange={(e) => setInventoryForm({ ...inventoryForm, complementaryValidityDate: e.target.value })}
                          className="w-full px-3 py-1.5 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-white [color-scheme:dark]"
                        />
                      </div>
                    </div>

                    <button
                      onClick={handleSaveInventory}
                      disabled={isSavingInventory}
                      className="mt-4 w-full py-2 bg-white text-blue-600 rounded-lg font-semibold hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-70"
                    >
                      {isSavingInventory ? (
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Save className="w-4 h-4" />
                      )}
                      Save Changes
                    </button>
                  </>
                )}
              </div>

              {/* Health Metrics Summary */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-green-600" />
                  Current Metrics
                </h3>
                <div className="space-y-3">
                  <MetricItem icon={TrendingUp} label="Weight" value={`${currentHealthMetrics.weight || bodyMeasurements.weightKg || 0} kg`} color="blue" />
                  <MetricItem icon={Droplets} label="Body Fat" value={`${currentHealthMetrics.bodyFat || bodyMeasurements.bodyFatPercentage || 0}%`} color="orange" />
                  <MetricItem icon={Heart} label="BMI" value={currentHealthMetrics.bmi || bodyMeasurements.bmi || 0} color="red" />
                  <MetricItem icon={Activity} label="Height" value={`${user.height || bodyMeasurements.height || bodyMeasurements.heightCm || 0} cm`} color="green" />
                  <MetricItem icon={Flame} label="BMR" value={`${bodyMeasurements.bmrKcal || 0} kcal`} color="purple" />
                </div>
              </div>

              {/* Total Orders */}
              <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-2xl shadow-lg p-6 text-white">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold">Total Orders</h3>
                  <ShoppingBag className="w-6 h-6 opacity-80" />
                </div>
                <div className="text-4xl font-bold mb-2">{orders.length}</div>
                <p className="text-purple-100 text-sm">Lifetime orders</p>
              </div>

              {/* Advanced Statistics */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-indigo-600" />
                  Consumption Statistics
                </h3>
                <div className="space-y-3">
                  <MetricItem icon={Package} label="Total Trial" value={`${totalComplementaryQty} shakes`} color="blue" />
                  <MetricItem icon={ShoppingBag} label="Total TopUp Purchased" value={`${totalTopupQty} shakes`} color="green" />
                  <MetricItem icon={User} label="Total Guest Shake" value={`${totalGuestShakeQty} shakes`} color="orange" />
                  <MetricItem icon={Users} label="Total Associate Shake" value={`${totalAssociateShakeQty} shakes`} color="purple" />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === "health" && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Body Measurements */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                  Body Measurements
                </h2>
                <div className="space-y-4">
                  <HealthMetricRow label="Weight" value={`${bodyMeasurements.weightKg || 0} kg`} icon={TrendingUp} />
                  <HealthMetricRow label="Body Fat" value={`${bodyMeasurements.bodyFatPercentage || 0}%`} icon={Droplets} />
                  <HealthMetricRow label="Visceral Fat" value={bodyMeasurements.visceralFat || 0} icon={Activity} />
                  <HealthMetricRow label="Muscle Mass" value={`${bodyMeasurements.muscleMassKg || 0} kg`} icon={Heart} />
                  <HealthMetricRow label="BMR" value={`${bodyMeasurements.bmrKcal || 0} kcal`} icon={Flame} />
                  <HealthMetricRow label="BMI" value={bodyMeasurements.bmi || 0} icon={Target} />
                  <HealthMetricRow label="Height" value={`${user.height || bodyMeasurements.height || bodyMeasurements.heightCm || 0} cm`} icon={Activity} />
                  <HealthMetricRow label="Body Age" value={bodyMeasurements.bodyAge || 0} icon={Calendar} />
                </div>
              </div>

              {/* Physical Measurements */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-purple-600" />
                  Physical Measurements
                </h2>
                <div className="space-y-4">
                  <HealthMetricRow label="Chest" value={`${bodyMeasurements.chestIn || 0} in`} icon={Activity} />
                  <HealthMetricRow label="Waist" value={`${bodyMeasurements.waistLineIn || 0} in`} icon={Activity} />
                  <HealthMetricRow label="Hip" value={`${bodyMeasurements.hipIn || 0} in`} icon={Activity} />
                </div>
                <div className="mt-6 p-4 bg-blue-50 rounded-xl">
                  <p className="text-sm text-blue-700">
                    <strong>Last Updated:</strong> {formatDate(currentHealthMetrics.lastUpdated)}
                  </p>
                </div>
              </div>
            </div>

            {/* Health Record History from Orders */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-green-600" />
                Health Record History
              </h2>
              {ordersLoading ? (
                <div className="text-center py-8">
                  <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-600">Loading health records...</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {orders
                    .filter(order => order.dailyHealthRecord)
                    .length === 0 ? (
                    <div className="text-center py-8">
                      <Activity className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                      <p className="text-gray-600 font-medium">No health records found</p>
                      <p className="text-gray-400 text-sm mt-2">Health records are captured when orders are placed</p>
                    </div>
                  ) : (
                    orders
                      .filter(order => order.dailyHealthRecord)
                      .map((order, index) => (
                        <HealthRecordCard
                          key={order.id || index}
                          order={order}
                          formatDate={formatDate}
                          isLatest={index === 0}
                        />
                      ))
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === "orders" && (
          <div className="space-y-4">
            <div className="flex justify-end mb-4">
              <select
                value={orderFilter}
                onChange={(e) => setOrderFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
              >
                <option value="All">All Orders</option>
                <option value="Guest Shake">Guest Shake</option>
                <option value="Associate Shake">Associate Shake</option>
                <option value="TopUp Guest Shake">TopUp Guest Shake</option>
                <option value="TopUp Associate Shake">TopUp Associate Shake</option>
                <option value="Trial">Trial</option>
                <option value="Donation">Donation</option>
                <option value="RetailShake">Retail Shake</option>
                <option value="Sample">Sample</option>
              </select>
            </div>

            {ordersLoading ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading orders...</p>
              </div>
            ) : orders.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">No orders found</p>
                <p className="text-gray-400 text-sm mt-2">This user hasn't placed any orders yet</p>
              </div>
            ) : (
              orders
                .filter(order => orderFilter === "All" || order.transaction?.type === orderFilter)
                .map((order) => (
                  <OrderCard key={order.id} order={order} formatDate={formatDate} formatCurrency={formatCurrency} />
                ))
            )}

            {/* Show a message if filtering results in empty */}
            {!ordersLoading && orders.length > 0 && orders.filter(order => orderFilter === "All" || order.transaction?.type === orderFilter).length === 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center text-gray-500">
                No {orderFilter} orders found.
              </div>
            )}
          </div>
        )}

        {activeTab === "herbalife" && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Herbalife Account */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-green-600" />
                Herbalife Account
              </h2>
              <div className="space-y-4">
                <InfoItem icon={User} label="Herbalife ID" value={herbalifeDetails.herbalifeId || herbalifeDetails.id} />
                <InfoItem icon={User} label="Last Name" value={herbalifeDetails.herbalifeLastName || herbalifeDetails.lastName} />
                <InfoItem icon={Mail} label="Email" value={herbalifeDetails.herbalifeEmail || herbalifeDetails.email} />
                <InfoItem icon={Award} label="Status" value={herbalifeDetails.preferredStatus || herbalifeDetails.preferredCustomerStatus} />
                <InfoItem icon={Calendar} label="Join Year" value={herbalifeDetails.joinYear} />
              </div>
            </div>

            {/* Sponsor Details */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-blue-600" />
                Sponsor Details
              </h2>
              <div className="space-y-4">
                <InfoItem icon={User} label="Sponsor Name" value={herbalifeDetails.sponsorDetails?.sponsorName || herbalifeDetails.sponsorName} />
                <InfoItem icon={User} label="Sponsor Last Name" value={herbalifeDetails.sponsorDetails?.sponsorLastName || herbalifeDetails.sponsorLastName} />
                <InfoItem icon={Award} label="Sponsor ID" value={herbalifeDetails.sponsorDetails?.sponsorId || herbalifeDetails.sponsorId} />
              </div>
            </div>
          </div>
        )}

        {activeTab === "referrals" && (
          <div className="space-y-4">
            {referralsLoading ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading referrals and their stats...</p>
              </div>
            ) : referrals.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">No referrals found</p>
                <p className="text-gray-400 text-sm mt-2">This user hasn't referred anyone yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {referrals.map((refUser) => (
                  <div key={refUser.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 border-b-2 border-transparent hover:border-blue-500 cursor-pointer transition-all" onClick={() => navigate(`/admin/users/${refUser.id}`)}>
                          {refUser.personalInformation?.fullName || refUser.personalDetails?.name || "Unknown"}
                        </h3>
                        <p className="text-xs text-gray-500">{refUser.personalInformation?.mobileNumber || refUser.personalDetails?.mobile || "No Mobile"}</p>
                      </div>
                    </div>

                    <div className="space-y-3 pt-4 border-t border-gray-100">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-gray-400" /> Total Orders</span>
                        <span className="font-semibold">{refUser.stats?.totalOrders || 0}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 flex items-center gap-2"><Package className="w-4 h-4 text-green-500" /> TopUp Guest</span>
                        <span className="font-semibold text-green-700">{refUser.stats?.topUpGuestQty || 0} shakes</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 flex items-center gap-2"><Package className="w-4 h-4 text-purple-500" /> TopUp Associate</span>
                        <span className="font-semibold text-purple-700">{refUser.stats?.topUpAssociateQty || 0} shakes</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "sponsored" && (
          <div className="space-y-4">
            {sponsoredLoading ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading sponsored users and their stats...</p>
              </div>
            ) : sponsoredUsers.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">No sponsored users found</p>
                <p className="text-gray-400 text-sm mt-2">This user hasn't sponsored anyone yet.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sponsoredUsers.map((sponUser) => (
                  <div key={sponUser.id} className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900 border-b-2 border-transparent hover:border-blue-500 cursor-pointer transition-all" onClick={() => navigate(`/admin/users/${sponUser.id}`)}>
                          {sponUser.personalInformation?.fullName || sponUser.personalDetails?.name || "Unknown"}
                        </h3>
                        <p className="text-xs text-gray-500">{sponUser.personalInformation?.mobileNumber || sponUser.personalDetails?.mobile || "No Mobile"}</p>
                      </div>
                    </div>

                    <div className="space-y-3 pt-4 border-t border-gray-100">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 flex items-center gap-2"><ShoppingBag className="w-4 h-4 text-gray-400" /> Total Orders</span>
                        <span className="font-semibold">{sponUser.stats?.totalOrders || 0}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 flex items-center gap-2"><Package className="w-4 h-4 text-green-500" /> TopUp Guest</span>
                        <span className="font-semibold text-green-700">{sponUser.stats?.topUpGuestQty || 0} shakes</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600 flex items-center gap-2"><Package className="w-4 h-4 text-purple-500" /> TopUp Associate</span>
                        <span className="font-semibold text-purple-700">{sponUser.stats?.topUpAssociateQty || 0} shakes</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "monthly" && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex items-center justify-between">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Calendar className="w-6 h-6 text-indigo-600" />
                Monthly Consumption Report
              </h2>
              <div>
                <input
                  type="month"
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-indigo-600"
                />
              </div>
            </div>

            {monthlyReportLoading ? (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-gray-600">Loading consumption details for {reportMonth}...</p>
              </div>
            ) : monthlyReportData ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="p-4 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <User className="w-5 h-5 text-blue-600" />
                      Personal Consumption
                    </h3>
                  </div>
                  <div className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-orange-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Package className="w-5 h-5 text-orange-500" />
                          <span className="font-medium text-gray-900">Guest Shake</span>
                        </div>
                        <span className="text-lg font-bold text-orange-700">{monthlyReportData.own.guest} shakes</span>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl">
                        <div className="flex items-center gap-3">
                          <Users className="w-5 h-5 text-purple-500" />
                          <span className="font-medium text-gray-900">Associate Shake</span>
                        </div>
                        <span className="text-lg font-bold text-purple-700">{monthlyReportData.own.associate} shakes</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden lg:row-span-2">
                  <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Users className="w-5 h-5 text-green-600" />
                      Referred Users Consumption
                    </h3>
                  </div>
                  <div className="p-4 bg-gray-50 border-b border-gray-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm text-center">
                        <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">Total Guest</div>
                        <div className="text-xl font-bold text-orange-600">{monthlyReportData.refTotals.guest}</div>
                      </div>
                      <div className="p-3 bg-white rounded-lg border border-gray-200 shadow-sm text-center">
                        <div className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-1">Total Associate</div>
                        <div className="text-xl font-bold text-purple-600">{monthlyReportData.refTotals.associate}</div>
                      </div>
                    </div>
                  </div>
                  <div className="p-0">
                    {monthlyReportData.referrals.length === 0 ? (
                      <div className="p-8 text-center text-gray-500">
                        No consumption by referred users in this month.
                      </div>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {monthlyReportData.referrals.map((r, idx) => (
                          <div key={idx} className="p-4 hover:bg-gray-50 flex items-center justify-between">
                            <div>
                              <h4 className="font-semibold text-gray-900">{r.name}</h4>
                              <p className="text-xs text-gray-500">{r.mobile}</p>
                            </div>
                            <div className="text-right flex gap-3 text-sm">
                              <div className="flex flex-col items-center">
                                <span className="text-[10px] text-gray-400 uppercase font-bold">Guest</span>
                                <span className="font-bold text-orange-600">{r.guestCount}</span>
                              </div>
                              <div className="flex flex-col items-center">
                                <span className="text-[10px] text-gray-400 uppercase font-bold">Assoc</span>
                                <span className="font-bold text-purple-600">{r.associateCount}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
}

// Helper Components
function InfoItem({ icon: Icon, label, value, className = "" }) {
  return (
    <div className={`${className}`}>
      <div className="flex items-start gap-3">
        {Icon && <Icon className="w-4 h-4 text-gray-400 mt-0.5" />}
        <div className="flex-1 min-w-0">
          <p className="text-xs text-gray-500 mb-1">{label}</p>
          <p className="text-sm font-medium text-gray-900 break-words">{value || "N/A"}</p>
        </div>
      </div>
    </div>
  );
}

function MetricItem({ icon: Icon, label, value, color }) {
  const colorClasses = {
    blue: "bg-blue-50 text-blue-600",
    orange: "bg-orange-50 text-orange-600",
    red: "bg-red-50 text-red-600",
    purple: "bg-purple-50 text-purple-600",
    green: "bg-green-50 text-green-600",
  };

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-4 h-4" />
        </div>
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function HealthMetricRow({ label, value, icon: Icon }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <span className="text-sm font-semibold text-gray-900">{value}</span>
    </div>
  );
}

function OrderCard({ order, formatDate, formatCurrency }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow">
      <div className="p-6">
        {/* Order Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-blue-600">{order.orderId || "N/A"}</h3>
              <span
                className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${order.transaction?.status === "completed"
                  ? "bg-green-100 text-green-700"
                  : "bg-gray-100 text-gray-700"
                  }`}
              >
                {order.transaction?.status || "N/A"}
              </span>
            </div>
            <p className="text-sm text-gray-500 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              {formatDate(order.timestamp || order.createdAt)}
            </p>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-blue-600 text-sm font-medium hover:text-blue-700"
          >
            {expanded ? "Hide Details" : "View Details"}
          </button>
        </div>

        {/* Order Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-blue-50 rounded-xl p-3">
            <p className="text-xs text-blue-600 mb-1">Transaction Type</p>
            <p className="text-sm font-semibold text-blue-900">{order.transaction?.type || "N/A"}</p>
          </div>
          <div className="bg-green-50 rounded-xl p-3">
            <p className="text-xs text-green-600 mb-1">Amount</p>
            <p className="text-sm font-semibold text-green-900">{formatCurrency(order.summary?.grandTotalAmount || 0)}</p>
          </div>
          <div className="bg-purple-50 rounded-xl p-3">
            <p className="text-xs text-purple-600 mb-1">Value Points</p>
            <p className="text-sm font-semibold text-purple-900">{(order.summary?.totalOrderVP || 0).toFixed(2)}</p>
          </div>
          <div className="bg-orange-50 rounded-xl p-3">
            <p className="text-xs text-orange-600 mb-1">Shake Balance</p>
            <p className="text-sm font-semibold text-orange-900">
              {order.summary?.shakeBalanceChange > 0 ? "+" : ""}
              {order.summary?.shakeBalanceChange || 0}
            </p>
          </div>
        </div>

        {/* Expanded Details */}
        {expanded && (
          <div className="border-t border-gray-100 pt-4 space-y-4">
            {/* Items */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Package className="w-4 h-4" />
                Items
              </h4>
              <div className="space-y-2">
                {order.items && order.items.length > 0 ? (
                  order.items.map((item, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{item.productName}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          Qty: {item.quantity} × {formatCurrency(item.pricePerUnit)} | VP: {item.valuePointPerUnit}/unit
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-gray-900">{formatCurrency(item.totalPrice)}</p>
                        <p className="text-xs text-purple-600">{item.totalValuePoints} VP</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">No items</p>
                )}
              </div>
            </div>

            {/* Health Record */}
            {order.dailyHealthRecord && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Activity className="w-4 h-4 text-green-600" />
                  Health Record
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-green-50 rounded-lg">
                  <div>
                    <p className="text-xs text-green-600">Weight</p>
                    <p className="text-sm font-semibold text-green-900">{order.dailyHealthRecord.weightKg} kg</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600">Body Fat</p>
                    <p className="text-sm font-semibold text-green-900">{order.dailyHealthRecord.bodyFatPct}%</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600">BMI</p>
                    <p className="text-sm font-semibold text-green-900">{order.dailyHealthRecord.bmi}</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600">BMR</p>
                    <p className="text-sm font-semibold text-green-900">{order.dailyHealthRecord.bmrKcal} kcal</p>
                  </div>
                  <div>
                    <p className="text-xs text-green-600">Height</p>
                    <p className="text-sm font-semibold text-green-900">{order.dailyHealthRecord.height || 0} cm</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Info */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-600">Payment Method</span>
              </div>
              <span className="text-sm font-medium text-gray-900 capitalize">
                {order.transaction?.paymentMethod || "N/A"}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function HealthRecordCard({ order, formatDate, isLatest }) {
  const record = order.dailyHealthRecord;

  return (
    <div className={`bg-white rounded-xl shadow-sm border-2 p-4 hover:shadow-md transition-shadow ${isLatest ? "border-green-500" : "border-gray-200"
      }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-semibold text-gray-900">
              Order: {order.orderId || "N/A"}
            </h4>
            {isLatest && (
              <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
                Latest
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(order.timestamp || order.createdAt)}
          </p>
        </div>
      </div>

      {/* Health Metrics Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="flex items-center gap-1 mb-1">
            <TrendingUp className="w-3 h-3 text-blue-600" />
            <p className="text-xs text-blue-600 font-medium">Weight</p>
          </div>
          <p className="text-sm font-bold text-blue-900">{record?.weightKg || 0} kg</p>
        </div>

        <div className="bg-orange-50 rounded-lg p-3">
          <div className="flex items-center gap-1 mb-1">
            <Droplets className="w-3 h-3 text-orange-600" />
            <p className="text-xs text-orange-600 font-medium">Body Fat</p>
          </div>
          <p className="text-sm font-bold text-orange-900">{record?.bodyFatPct || 0}%</p>
        </div>

        <div className="bg-red-50 rounded-lg p-3">
          <div className="flex items-center gap-1 mb-1">
            <Heart className="w-3 h-3 text-red-600" />
            <p className="text-xs text-red-600 font-medium">BMI</p>
          </div>
          <p className="text-sm font-bold text-red-900">{record?.bmi || 0}</p>
        </div>

        <div className="bg-purple-50 rounded-lg p-3">
          <div className="flex items-center gap-1 mb-1">
            <Flame className="w-3 h-3 text-purple-600" />
            <p className="text-xs text-purple-600 font-medium">BMR</p>
          </div>
          <p className="text-sm font-bold text-purple-900">{record?.bmrKcal || 0} kcal</p>
        </div>

        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center gap-1 mb-1">
            <Activity className="w-3 h-3 text-green-600" />
            <p className="text-xs text-green-600 font-medium">Height</p>
          </div>
          <p className="text-sm font-bold text-green-900">{record?.height || 0} cm</p>
        </div>
      </div>

      {/* Additional Metrics if available */}
      {(record?.visceralFat || record?.muscleMassKg) && (
        <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-100">
          {record?.visceralFat && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Visceral Fat</span>
              <span className="text-xs font-semibold text-gray-900">{record.visceralFat}</span>
            </div>
          )}
          {record?.muscleMassKg && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-gray-600">Muscle Mass</span>
              <span className="text-xs font-semibold text-gray-900">{record.muscleMassKg} kg</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
